import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTierState } from '../hooks/useTierState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRIAL_DAYS = 14;
const DISMISS_KEY = 'enferno_verify_banner_dismissed_session';

// Visibility rules:
//   - email_verified = true                 → never (nothing to nudge)
//   - paying / lifetime / admin (verified)  → never
//   - app_trial unverified, day 0-10 left   → dismissable soft banner
//   - app_trial unverified, day 0-3 left    → non-dismissable insistent banner
//   - free (trial expired) unverified       → HARD BLOCK handled by backend
//                                             (this banner stays visible too as a
//                                             reminder, non-dismissable, urgent tone)
//
// Backend's `require_verified_email` lets every paid path through, so a paying
// user is never blocked — they just see the soft nudge.

const VerifyEmailBanner = () => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { reason: tierReason, loading: tierLoading } = useTierState();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [resending, setResending] = useState(false);

  const daysLeft = useMemo(() => {
    if (!profile.onboardingCompletedAt) return null;
    const completed = new Date(profile.onboardingCompletedAt).getTime();
    if (Number.isNaN(completed)) return null;
    const elapsedDays = (Date.now() - completed) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.ceil(TRIAL_DAYS - elapsedDays));
  }, [profile.onboardingCompletedAt]);

  if (!user) return null;
  if (user.email_verified) return null;
  if (tierLoading) return null;
  // Lifetime / admin / paid subscribers shouldn't be nudged at all — we still
  // want them to verify, but the existing TrialBanner doesn't bother them,
  // and they have other channels (we can reach paying users by support).
  if (['admin', 'founder', 'hero', 'comp', 'subscription', 'subscription_grace'].includes(tierReason)) return null;
  if (!profile.completedOnboarding) return null;
  if (daysLeft === null) return null;

  const isFinal = daysLeft <= 3;
  const isExpired = daysLeft === 0 || tierReason === 'free';
  // Final 3 days OR trial expired → not dismissable. Earlier in trial → soft.
  const allowDismiss = !isFinal && !isExpired;
  if (allowDismiss && dismissed) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: user.email });
      toast.success('We sent a fresh link', {
        description: `Check ${user.email}. First-time emails sometimes land in spam — check that folder too.`,
        duration: 5500,
      });
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error('Too many requests. Wait a minute, then try again.');
      } else {
        toast.error('Could not send a new link. Try again in a moment.');
      }
    } finally {
      setResending(false);
    }
  };

  let label;
  if (isExpired) label = `Confirm your email to keep using Enferno — we sent a link to ${user.email}.`;
  else if (isFinal) label = `Confirm your email before your trial ends — only ${daysLeft} day${daysLeft === 1 ? '' : 's'} left.`;
  else label = 'Confirm your email so we can keep your account safe.';

  return (
    <div
      className={`mb-4 rounded-lg border px-3 py-2 flex items-center justify-between gap-3 text-xs ${
        isExpired || isFinal
          ? 'bg-enf-accent/10 border-enf-accent-fg/40'
          : 'bg-enf-surface-mid border-enf-surface-border'
      }`}
      data-testid="verify-email-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Mail className={`w-3.5 h-3.5 flex-shrink-0 ${isExpired || isFinal ? 'text-enf-accent-fg' : 'text-enf-text-muted'}`} />
        <span className={`truncate ${isExpired || isFinal ? 'text-enf-text-primary font-semibold' : 'text-enf-text-secondary'}`}>
          {label}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className={`px-2.5 py-1 rounded font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-60 ${
            isExpired || isFinal
              ? 'bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary'
              : 'bg-enf-surface-elevated hover:bg-enf-surface-elevated text-enf-text-primary'
          }`}
          data-testid="verify-email-banner-resend"
        >
          {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          Resend
        </button>
        {isExpired && (
          <button
            type="button"
            onClick={() => navigate('/verify-email')}
            className="px-2.5 py-1 rounded font-semibold transition-colors bg-enf-surface-elevated hover:bg-enf-surface-elevated text-enf-text-primary"
            data-testid="verify-email-banner-open"
          >
            Open
          </button>
        )}
        {allowDismiss && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="p-1 text-enf-text-muted hover:text-enf-text-primary transition-colors"
            data-testid="verify-email-banner-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailBanner;
